import { useCallback, useState } from 'react';

import { IKycDocumentReviewModalProps } from '@/features/kyc/store/types/kyc-need-review.types';
import { IKycDocumentDeleteModalProps } from '@/features/kyc/store/types/kyc-documents.types';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAccesControl } from '@/hooks/useAccessControl';
import { openModal } from '@/store/actions/core.actions';
import { selectProfileKycDocumentByUserIdAndDocumentId } from '@/store/selectors';
import { useProfileContext } from '@/features/context/ProfileContext';
import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { TComputedKycDocument } from '@/features/kyc/store/models/kyc.model';
import { getPreviewKycDocument } from '@/features/kyc/store/actions/kyc-profile.actions';
import { useKycDownloadDocument } from '@/features/kyc/hooks/useKycDownloadDocument';
import { IKycDocumentDetailModalProps } from '@/features/kyc/components/modals/KycDocumentDetailModal';

interface IUseKycDocumentActionsProps {
  document: TComputedKycDocument;
}

export function useKycDocumentActions({ document }: IUseKycDocumentActionsProps) {
  const { user } = useProfileContext();
  const dispatch = useAppDispatch();
  const { hasAccessToFeature } = useAccesControl();
  const config = useDashboardConfig();

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isPending, triggerDownloadDocument } = useKycDownloadDocument({ document });
  const isLoadingPreview = useAppSelector(
    (state) =>
      selectProfileKycDocumentByUserIdAndDocumentId(state, user.userId, document.documentId)?.preview?.isLoading,
  );

  const preview = useAppSelector(
    (state) => selectProfileKycDocumentByUserIdAndDocumentId(state, user.userId, document.documentId)?.preview?.state,
  );

  const isKnownByConnect = document.isAgent || !!document.metadata?.connectDocumentId;
  const isDownloadable = config?.settings.users?.kyc?.documentTypeId?.includes(document.documentType);

  const viewKycDocument = useCallback(() => {
    if (preview) {
      DashboardHelpers.downloadFile(preview.url, document.fileName || ``, true);
      return;
    }

    if (isKnownByConnect) {
      dispatch(
        getPreviewKycDocument(document.userId, document, (url) =>
          DashboardHelpers.downloadFile(url, document.fileName || ``, true),
        ),
      );
      return;
    }

    if (!isKnownByConnect && isDownloadable) {
      triggerDownloadDocument();
      return;
    }
  }, [preview, isDownloadable, isKnownByConnect, dispatch, document, triggerDownloadDocument]);

  const reviewKycDocument = () => {
    !preview && dispatch(getPreviewKycDocument(document.userId, document));
    dispatch(openModal<IKycDocumentReviewModalProps>('kycDocumentReviewModal', { document, user }, { maxWidth: 'xl' }));
  };

  const isDisabled = !isKnownByConnect && !isDownloadable;

  const seeKycDocumentDetails = () => {
    !preview && isKnownByConnect && dispatch(getPreviewKycDocument(document.userId, document));
    dispatch(
      openModal<IKycDocumentDetailModalProps>(
        'kycDocumentDetailModal',
        {
          document,
          hasPreview: !isDisabled,
          hasComment: isKnownByConnect,
          shouldDownloadDocument: !isKnownByConnect && isDownloadable,
        },
        { maxWidth: 'xl' },
      ),
    );
  };

  const deleteKycDocument = () => {
    dispatch(
      openModal<IKycDocumentDeleteModalProps>(
        'kycDocumentDeleteModal',
        {
          document,
          user,
        },
        { maxWidth: 'xl' },
      ),
    );
  };

  const dropdownActions = [
    ...(hasAccessToFeature(featuresSlugs.REVIEW_DOCUMENT) && document.kycStatus === 'TO_REVIEW'
      ? [{ key: 'reviewDocument', onClick: reviewKycDocument }]
      : []),
    ...(hasAccessToFeature(featuresSlugs.DELETE_DOCUMENT) && document.kycStatus === 'TO_REVIEW'
      ? [
          {
            key: 'deleteDocument',
            onClick: deleteKycDocument,
          },
        ]
      : []),
    ...(hasAccessToFeature(featuresSlugs.PROFILES_BOARD) && document.kycStatus !== 'TO_REVIEW'
      ? [{ key: 'seeDocumentDetails', onClick: seeKycDocumentDetails }]
      : []),
  ];

  return {
    isMenuOpen,
    toggleMenu: () => setIsMenuOpen((open) => !open),
    isDisabled,
    isLoading: isLoadingPreview || isPending,
    viewKycDocument,
    dropdownActions,
  };
}
